import React from 'react';
import parse from 'html-react-parser';
// conponents
import Accordion from 'react-bootstrap/Accordion';
import Image from '../image/Image';
import TrustpilotWidget from './trustpilot-widget';
// content
import footerData from './footer-content';
import { trademark } from '../../documents/disclosures/regulatory';
// scripts
import { placeholderDarker } from '../../scripts/media';
import { appStoreAppLink, googlePlayAppLink, sharedLogosPath } from '../../scripts/data';
// interfaces
import { IColLInk, IMainCol } from './footer-interfaces';
import { pageType } from '../../interfaces';
import CustomLink from '../custom-link/CustomLink';

type FooterProps = {
  homepage?: boolean;
  tradeMarkFootNote?: string;
  disclosures?: React.ReactNode;
  pageType: pageType;
};

export default class Footer extends React.Component<FooterProps> {
  render() {
    const id = 'footer';
    const footerDataUpdated = () => {
      return footerData.mainCols
        .map((col) => {
          const newCol: IMainCol = { ...col };
          newCol.links = newCol.links.filter((el: IColLInk) => {
            if (this.props.homepage) {
              return (
                (Object.prototype.hasOwnProperty.call(el, 'homepage') && el.homepage) ||
                !Object.prototype.hasOwnProperty.call(el, 'homepage')
              );
            }
            return (
              (Object.prototype.hasOwnProperty.call(el, 'homepage') && !el.homepage) ||
              !Object.prototype.hasOwnProperty.call(el, 'homepage')
            );
          });
          return newCol;
        })
        .slice(0, this.props.homepage ? undefined : -1); // Remove the last element if not homepage
    };
    const returnFootNote = () => {
      return this.props.tradeMarkFootNote ? parse(this.props.tradeMarkFootNote) : '';
    };
    return (
      <footer
        id={id}
        className="bg-light pt-6 pt-md-7 pt-lg-8">
        <div className="container ">
          <Accordion
            alwaysOpen
            flush
            className="d-lg-flex links-unstyled justify-content-lg-between accordion-lg-none accordion-angle-icon">
            {!this.props.homepage ? (
              <div className="d-md-flex d-lg-block col-lg-3 mb-5 mb-lg-0">
                <CustomLink
                  isLink={this.props.pageType === 'product'}
                  href="/"
                  className="d-block mb-4 mb-md-0 mb-lg-4 me-md-4 me-lg-0 flex-shrink-0">
                  <Image
                    src={`${sharedLogosPath}/logo-experian.svg`}
                    alt="Experian logo."
                    width="120"
                    height="40"
                    className="img-fluid exp-logo"
                  />
                </CustomLink>
                <p className="text-gray mb-0">
                  Experian is a globally recognized financial leader, committed to being a Big
                  Financial Friend—empowering millions to take control of their finances through
                  expert guidance and innovative tools. As a trusted platform for money management,
                  credit education, and identity protection, our mission is to bring Financial Power
                  to All™.
                </p>
              </div>
            ) : null}
            {footerDataUpdated().map((col, index: number) => (
              <Accordion.Item
                eventKey={`${index}${id}`}
                key={col.id}>
                <p className="d-none d-lg-block h7 text-secondary">{col.column}</p>
                <Accordion.Button
                  className="d-lg-none mb-0 h7"
                  id={`${index}${id}h`}
                  aria-controls={`${index}${id}`}>
                  {col.column}
                </Accordion.Button>
                <Accordion.Body
                  as="ul"
                  aria-describedby={`${index}${id}h`}
                  id={`${index}${id}`}
                  className="list-unstyled">
                  {col.links.map((link: IColLInk) => (
                    <li key={link.id}>
                      <CustomLink
                        isLink={link.linkType === this.props.pageType}
                        href={`${link.link}`}
                        {...link?.attributes}>
                        {parse(link.title)}
                      </CustomLink>
                    </li>
                  ))}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
          <div className="d-lg-flex text-center text-lg-start mt-lg-5 py-6 justify-content-lg-between mid-wrapper">
            <div className="d-flex flex-wrap flex-xl-nowrap">
              <div className="d-lg-flex mb-4 mb-xl-0 pe-xl-7 w-100 w-xl-auto">
                <Image
                  src={`${sharedLogosPath}/logo-experian-dei.svg`}
                  className="me-lg-2 mb-2 mb-lg-0"
                  width="32"
                  height="32"
                  alt="Experian's Diversity logo."
                  lazyLoading
                  placeholder={placeholderDarker}
                />
                <div>
                  <div className="eyebrow-text">Experian’s Diversity, Equity and Inclusion</div>
                  <a
                    href="/diversity/"
                    data-link-both="dei|footer-banner">
                    Learn more how Experian is committed
                  </a>
                </div>
              </div>
              <div className="d-lg-flex px-xl-0 w-100 w-xl-auto">
                <Image
                  src={`${sharedLogosPath}/logo-data-privacy.svg`}
                  className="me-lg-2 mb-2 mb-lg-0"
                  width="35"
                  height="20"
                  alt="Data privacy logo."
                  lazyLoading
                  placeholder={placeholderDarker}
                />
                <div>
                  <div className="eyebrow-text">Data privacy</div>
                  <a
                    href="https://consumerprivacy.experian.com"
                    target="_blank"
                    rel="noopener noreferrer">
                    Your privacy choices
                  </a>
                </div>
              </div>
            </div>
            <div>
              <div className="d-lg-flex flex-lg-nowrap">
                <TrustpilotWidget />
                <div className="app-badge d-flex justify-content-center mt-5 mt-lg-0 ">
                  <a
                    href={appStoreAppLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-block pe-2"
                    data-op="IBF">
                    <Image
                      width="120"
                      lazyLoading
                      height="36"
                      src={`${sharedLogosPath}/logo-app-badge-apple.svg`}
                      alt="Download from the Apple App Store."
                      placeholder={placeholderDarker}
                    />
                  </a>
                  <a
                    href={googlePlayAppLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-op="ABF"
                    className="d-block">
                    <Image
                      width="120"
                      height="36"
                      lazyLoading
                      src={`${sharedLogosPath}/logo-badge-google-play.svg`}
                      alt="Get it on Google Play."
                      placeholder={placeholderDarker}
                    />
                  </a>
                </div>
              </div>
              <ul className="mt-5 mt-lg-4 d-flex list-unstyled mb-0 justify-content-center justify-content-lg-end">
                {footerData.social.map((item, i) => (
                  <li
                    key={item.id}
                    className={`mb-0${i !== footerData.social.length - 1 ? ' me-3' : ''}`}>
                    <a
                      className="btn btn-reset font-icon-size-lg"
                      aria-label={item.label}
                      title={item.label}
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer">
                      <span
                        className={item.icon}
                        aria-hidden="true"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bottom-wrapper links-unstyled">
            <ul className="list-unstyled text-center d-lg-flex justify-content-lg-between">
              {footerData.legal.map((item) => (
                <li key={item.id}>
                  <a
                    href={item.link}
                    {...(item.target && { target: item.target })}
                    {...(item.rel && { rel: item.rel })}>
                    {item.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div
            className="pb-5 pt-3 pb-md-7 pt-md-4"
            id="footer-disclosures">
            {this.props.disclosures ? this.props.disclosures : null}
            <p>{parse(`© ${new Date().getFullYear()} Experian. All rights reserved.`)}</p>
            <p>
              {returnFootNote()}
              {parse(trademark)}
            </p>
          </div>
        </div>
      </footer>
    );
  }
}
